import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatISO, isValid, parseISO } from 'date-fns';
import React from 'react';
/**
 * A Material UI-based date picker component for selecting a single date.
 *
 * @component
 *
 * @param {DatePickerProps} props - The props for the DatePicker component.
 * @param {SxProps} [props.sx] - Optional style overrides using the MUI `sx` prop.
 * @param {string} props.label - The label to display for the date picker.
 * @param {string} [props.minDate] - Minimum allowed date (ISO format) as a visual hint.
 * @param {string} [props.maxDate] - Maximum allowed date (ISO format) as a visual hint.
 * @param {boolean} [props.required] - Whether the field is required.
 * @param {boolean} [props.disabled] - Disables the field if true.
 * @param {boolean} [props.readOnly] - Sets the field as read-only if true.
 * @param {boolean} [props.fullWidth] - If true, the input will take up full width.
 * @param {'small' | 'medium'} [props.size='medium'] - Size of the input field.
 * @param {boolean} [props.error] - Marks the field with an error state.
 * @param {number} [props.tabIndex] - Sets the tabIndex for keyboard navigation.
 * @param {DatePickerValue} props.value - The current value of the date picker.
 * @param {boolean} [props.hideLabel] - If true, hides the label visually.
 * @param {(newValue: DatePickerValue) => void} props.onChange - Callback triggered when date value changes.
 * @param {string} [props.'data-test'] - Optional test selector for automation.
 * @param {boolean} [props.autoFocus] - If true, focuses the input automatically on mount.
 * @param {string} [props.ariaDescribedBy] - ARIA attribute to describe the input.
 *
 * @returns {React.ReactElement} Rendered date picker component.
 */
export const DatePicker = (props) => {
    var _a;
    const [focused, setFocused] = React.useState(false);
    let value;
    const dateRangeProps = {};
    if (props.value.invalidDate) {
        value = props.value.invalidDate;
    }
    else if (props.value.date != null) {
        value = parseISO(props.value.date);
    }
    else {
        value = null;
    }
    let minDate;
    if (props.minDate) {
        minDate = parseISO(props.minDate);
        if (!isValid(minDate)) {
            minDate = undefined;
        }
    }
    let maxDate;
    if (props.maxDate) {
        maxDate = parseISO(props.maxDate);
        if (!isValid(maxDate)) {
            maxDate = undefined;
        }
    }
    if (minDate) {
        dateRangeProps.minDate = minDate;
    }
    if (maxDate) {
        dateRangeProps.maxDate = maxDate;
    }
    return (React.createElement(MuiDatePicker, Object.assign({ label: (value != null || focused) && props.hideLabel ? '' : props.label, value: value, disabled: props.disabled, readOnly: props.readOnly }, (Object.keys(dateRangeProps).length > 0 ? dateRangeProps : {}), { slotProps: {
            textField: {
                onFocus: () => {
                    setFocused(true);
                },
                onBlur: () => {
                    setFocused(false);
                },
                fullWidth: props.fullWidth,
                error: props.error,
                required: props.required,
                size: props.size,
                autoComplete: 'off',
                tabIndex: props.tabIndex,
                InputLabelProps: !props.hideLabel ? undefined : { shrink: false },
                inputProps: { 'aria-label': props.label, 'aria-describedby': props.ariaDescribedBy },
                sx: Object.assign({ width: '100%', maxWidth: {
                        xs: '100%',
                        sm: props.fullWidth ? '100%' : 260,
                    }, '& label.MuiInputLabel-root': { paddingRight: 3 } }, props.sx),
                autoFocus: (_a = props.autoFocus) !== null && _a !== void 0 ? _a : false,
            },
        }, onChange: (newDate) => {
            if (newDate != null) {
                if (isValid(newDate)) {
                    props.onChange({ date: formatISO(newDate, { representation: 'date' }) });
                }
                else {
                    props.onChange({ invalidDate: newDate });
                }
            }
            else {
                props.onChange({});
            }
        } })));
};
